/// <amd-module name="Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual" />
import { AnalyticsDataValidator } from "Analytics/Shared/Common//AnalyticsDataValidator";
import { ViewType } from "Analytics/Shared/Common/Types";
import { ITrendData, TrendType } from "Analytics/Shared/Common/Types";
import * as Utils_Number from "Analytics/Shared/Utils/Number";
import * as Resources from "Analytics/Shared/Visuals/CardMetricsVisual/Resources";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { CardContent, CardFooter, CustomCard } from "VSSUI/Card";
import { Header } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css, KeyCode } from "VSSUI/Util";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";

export interface IPrimaryMetric {
    suffix?: string;
    value: string | JSX.Element;
}

export interface ISecondaryMetric {
    label?: string;
    value: string | JSX.Element;
    footer?: string;
}

/**
 * Enum describing font sizes to be displayed by the component on the secondary metric.
 * Defaults are Medium for the label and Large for the value.
 */
export enum MetricSize {
    Small,
    Medium,
    Large,
    XLarge,
    XXLarge,
    XXXLarge
}

export interface ICardMetricsProps extends IVssComponentProperties {
    title: string;
    dataValidationPromises: Promise<any>[] | undefined;
    onCardClick: (event?: any) => void;
    primaryMetric: IPrimaryMetric | undefined;
    secondaryMetric: ISecondaryMetric | undefined;
    secondaryMetricSize?: {
        label: MetricSize;
        value: MetricSize;
    };
    trend?: ITrendData;
    isTrendGood?: boolean;
    insight: string;
    errorText?: string;
    info?: string;
    iconName?: string;
    linkTarget?: string;
    onLinkClick?: (event?: any) => void;
}

export class CardMetricsVisual extends VssComponent<ICardMetricsProps> {
    public onClick?: (event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => void;
    public render() {
        return (
            <div
                className={css("analytics-cardmetrics-area flex-column flex-grow", this.props.className || "")}
                onClick={this._onCardClick}
                onKeyDown={this._onCardKeyDown}
                tabIndex={0}
                role={"region"}
            >
                <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                    <CustomCard className="flex-grow analytics-card">
                        <Header
                            className="analytics-card-header bolt-card-header"
                            title={this.props.title}
                            titleAriaLevel={2}
                            description={this._checkIfMetricDataPresent() && !this.props.errorText ? this.props.subtitle : ""}
                        />

                        <CardContent contentPadding={false}>
                            <AnalyticsDataValidator
                                dataValidationPromises={this.props.dataValidationPromises!}
                                compactView={true}
                                getMessage={this._getMessage}
                                getSuggestion={this._getSuggestion}
                            >
                                <div className="flex-column flex-grow">
                                    {this._getElementToDisplay()}
                                    {this.props.children}
                                </div>
                            </AnalyticsDataValidator>
                        </CardContent>

                        <CardFooter className="analytics-card-footer">
                            <div className="flex-column flex-grow flex-self-end">
                                {this.props.insight && this._checkIfMetricDataPresent() && (
                                    <div className="card-metrics-insight flex-row font-size-m font-weight-normal secondary-text">
                                        <div className="flex-self-start">
                                            <Icon className="card-metric-insight-icon icon-margin font-size-ml" iconName="CRMCustomerInsightsApp" />
                                        </div>
                                        <span
                                            className="card-metrics-insight-text"
                                            dangerouslySetInnerHTML={{
                                                __html: filterXSS(this.props.insight, xssStyleDefaultOptions)
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="flex-row">
                                    {this.props.linkTarget && (
                                        <Link
                                            className="flex flex-grow flex-self-end"
                                            onClick={this._onLinkClick}
                                            target="_blank"
                                            href={this.props.linkTarget}
                                        >
                                            {Resources.LearnMore}
                                        </Link>
                                    )}
                                    {!this.props.errorText && (
                                        <div className="flex flex-grow view-report font-size-m font-weight-semibold">
                                            <Icon className="icon-margin" iconName="ChevronRight" />
                                            <span>{Resources.ViewFullReport}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardFooter>
                    </CustomCard>
                </ErrorBoundary>
            </div>
        );
    }

    private _onLinkClick = (event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void => {
        event.stopPropagation();
        if (this.props.onLinkClick) {
            this.props.onLinkClick(event);
        }
    };

    private _getPrimaryMetricElement(primaryMetric: IPrimaryMetric, trend?: ITrendData): JSX.Element {
        return (
            <div className="card-metrics-element flex-row">
                <div className="card-metrics-value font-size-lll flex-self-end">
                    {primaryMetric.value}
                    {primaryMetric.suffix && <span className="font-size-ll">{primaryMetric.suffix}</span>}
                </div>
                {trend && (
                    <div
                        className={css(
                            "pipeline-trend flex-self-end font-weight-semibold",
                            this.props.isTrendGood
                                    ? "positive-color"
                                    : "negative-color"
                        )}
                    >
                        <Icon
                            className="trend-icon font-size-xs"
                            iconName={trend.trendType === TrendType.Increased ? "TriangleSolidUp12" : "TriangleSolidDown12"}
                        />
                        <span className="font-size-m">
                            {localeFormat(Resources.TrendFormat, Utils_Number.formatAbbreviatedNumber(trend.percentageVariation || 0))}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    private getFontClassName(size: MetricSize): string {
        let className = "";
        switch (size) {
            case MetricSize.Small: {
                className = "fontSizeS";
                break;
            }
            case MetricSize.Medium: {
                className = "font-size-m";
                break;
            }
            case MetricSize.Large: {
                className = "font-size-l";
                break;
            }
            case MetricSize.XLarge: {
                className = "font-size-xl";
                break;
            }
            case MetricSize.XXLarge: {
                className = "font-size-xxl";
                break;
            }
            case MetricSize.XXXLarge: {
                className = "font-size-xxxl";
                break;
            }
        }
        return className;
    }

    private _getMetricElement(metricData: { label?: string; value: string | JSX.Element; footer?: string }): JSX.Element {
        let metricLabelSize = "font-size-m";
        let metricValueSize = "body-xl";
        const size = this.props.secondaryMetricSize;
        if (size) {
            if (size.label !== undefined) {
                metricLabelSize = this.getFontClassName(size.label);
            }
            if (size.value !== undefined) {
                metricValueSize = this.getFontClassName(size.value);
            }
        }
        return (
            <div className="card-metrics-element flex-column">
                <div className={`card-metrics-label ${metricLabelSize} secondary-text`}>{metricData.label}</div>
                <div className={`card-metrics-value ${metricValueSize} font-weight-semibold`}>{metricData.value}</div>
                {metricData.footer && <div className="font-size-m secondary-text">{metricData.footer}</div>}
            </div>
        );
    }

    private _getElementToDisplay(): JSX.Element {
        if (this.props.errorText) {
            return (
                <AnalyticsUnavailableMessage
                    message={Resources.ErrorMessage}
                    suggestion={this.props.errorText}
                    iconName={this.props.iconName}
                    messageClassName={"ax-unavailable-error-message"}
                />
            );
        }

        if (this.props.info) {
            return <AnalyticsUnavailableMessage info={this.props.info} iconName={this.props.iconName} />;
        }

        if (this._checkIfMetricDataPresent()) {
            return (
                <>
                    <div className="flex-row flex-wrap bolt-default-horizontal-spacing">
                        {this.props.primaryMetric && this._getPrimaryMetricElement(this.props.primaryMetric, this.props.trend)}
                        {this.props.secondaryMetric && this._getMetricElement(this.props.secondaryMetric)}
                    </div>
                </>
            );
        } else {
            return <Spinner className="card-spinner flex-column flex-grow" size={SpinnerSize.large} />;
        }
    }

    private _onCardClick = (event?: React.MouseEvent<HTMLDivElement>): void => {
        if (this.props.onCardClick) {
            this.props.onCardClick(event);
        }
    };

    private _onCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.keyCode === KeyCode.enter || event.keyCode === KeyCode.space) {
            this.props.onCardClick(event);
        }
    };

    private _getMessage = (viewType: ViewType): string | undefined => {
        if (viewType === ViewType.DataNotReadyView) {
            return undefined;
        }
        return "";
    };

    private _getSuggestion = (viewType: ViewType, errorText?: string): string | undefined => {
        if (viewType === ViewType.DataNotReadyView) {
            return Resources.DataNotReady;
        } else if (viewType === ViewType.ErrorView) {
            return errorText!;
        } else {
            return undefined;
        }
    };

    private _checkIfMetricDataPresent = (): boolean => {
        return this.props.primaryMetric != null || this.props.secondaryMetric != null;
    };
}
